import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    InformationCard,
    InformationCardProps,
    NoteInformation,
    Previous,
    Switch,
    CardHeader,
    CardSection,
    FlexyButton,
} from '@europrocurement/flexy-components';
import {
    CollectionDossierFormalite,
    DossierJsonldRead,
    PrescripteurApi,
    SousClientApi,
    useFormaliteService,
    getDossierLibelle,
} from '@europrocurement/l2d-domain';
import { alpha, Box, Grid, TextField, Typography, useTheme } from '@mui/material';

import { sendIcon } from '@europrocurement/l2d-icons';
import RecapDocuments from './Docs/RecapDocuments';
import RecapLignesArticles from './RecapLignesArticles';
import RecapBillingBody from './RecapBillingBody';

type RecapFormalityShowProps = {
    collection: CollectionDossierFormalite;
    onPrevious?: () => void;
    onSubmit?: (collection: CollectionDossierFormalite, commentaires: string | null) => void;
    onChange?: (
        collection: CollectionDossierFormalite,
        refInterne: string | null,
        commentaires: string | null,
        dossierUrgent: boolean | null,
        indirectSale: boolean,
        recipientId: number,
    ) => void;
    prescripteur: PrescripteurApi;
    sousClient?: SousClientApi | undefined;
    sousClients: SousClientApi[];
    commentairesDossier?: string | null;
};

const RecapFormalityShow: React.FunctionComponent<RecapFormalityShowProps> = function ({
    collection,
    prescripteur,
    sousClient = prescripteur,
    sousClients,
    onPrevious,
    onSubmit,
    onChange,
    commentairesDossier,
}) {
    if (!collection.dossiers || collection.dossiers.length === 0) {
        console.error("Demande d'affichage d'une collection sans dossier en récap.");
        throw new Error("Demande d'affichage d'une collection sans dossier en récap.");
    }
    if (collection.dossiers.length > 1) {
        console.error("Demande d'affichage d'une collection multiples en récap.");
        throw new Error("Demande d'affichage d'une collection multiples en récap.");
    }

    const premierDossier = collection?.dossiers[0];
    const { dossierApi } = useFormaliteService();
    const theme = useTheme();

    const [commentaires, setCommentaires] = useState<string | null>(commentairesDossier || null);
    const [innerDossier, setInnerDossier] = useState<DossierJsonldRead | null>(null);
    const [dossierUrgent, setDossierUrgent] = useState<boolean>(collection.urgent as boolean);

    if (premierDossier.client === undefined || premierDossier.client === null) {
        console.error("Demande d'affichage d'un dossier sans client en récap");
        throw new Error("Demande d'affichage d'un dossier sans client en récap");
    }
    if (premierDossier.clientOpere === undefined || premierDossier.clientOpere === null) {
        console.error("Demande d'affichage d'un dossier sans client opéré en récap");
        throw new Error("Demande d'affichage d'un dossier sans client opéré en récap");
    }
    const [refInterne, setRefInterne] = useState<string | null>(
        premierDossier.societeRef ? premierDossier.societeRef : null,
    );
    const [refInterneError, setRefInterneError] = useState('');

    const onSubmitHandler = useCallback(() => {
        if (onSubmit) {
            onSubmit(collection, commentaires);
        }
    }, [collection, commentaires, onSubmit]);

    useEffect(() => {
        if (premierDossier.id) {
            console.log('L 277 setInnerDossier aoiDossiersIdGet');
            dossierApi.apiDossiersIdGet({ id: `${premierDossier.id}` }).then(({ data }) => {
                setInnerDossier(data);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [premierDossier.id]);

    let cardItemsSocite: InformationCardProps['items'] = [
        {
            label: 'Société concernée',
            value: getDossierLibelle(premierDossier),
        },
    ];

    if (premierDossier.societeSiren) {
        cardItemsSocite = [
            ...cardItemsSocite,
            {
                label: 'Numéro Siren',
                value: premierDossier.societeSiren,
            },
        ];
    }

    const cardProps = useMemo(
        () => ({
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0px 2px 2.5px 0px ${theme.palette.background.shadow}`,
        }),
        [theme.palette.background.paper, theme.palette.background.shadow],
    );

    return (
        <>
            {onPrevious ? <Previous onClick={onPrevious} /> : null}
            <Typography
                variant="l2dh4"
                sx={{
                    marginBottom: '10px',
                }}
            >
                Récapitulatif du dossier #{premierDossier.numero}
            </Typography>
            <Grid
                container
                flexWrap="nowrap"
                spacing={6}
            >
                <Grid
                    item
                    lg={8}
                    display="grid"
                    gap="16px"
                >
                    <InformationCard
                        title="Société"
                        subtitle=""
                        items={cardItemsSocite}
                        sx={{ ...cardProps }}
                    />
                    <InformationCard
                        title="Formalité"
                        items={
                            !premierDossier.prestations || premierDossier.prestations.length === 0
                                ? []
                                : premierDossier.prestations?.map((prestation, index) => ({
                                      label: `Formalité n°${index + 1}`,
                                      value: prestation.libelle,
                                  }))
                        }
                        sx={{ ...cardProps }}
                        subtitle="Pour modifier cette formalité, contactez votre formaliste"
                    />
                    <CardSection
                        sx={{ margin: 0 }}
                        sxContent={{ gap: '24px', display: 'grid' }}
                        header={
                            <CardHeader
                                headerSx={{ paddingTop: '24px' }}
                                sx={{
                                    minHeight: '36px',
                                    borderLeft: `solid 2px ${theme.palette.primary.main}`,
                                }}
                                title={
                                    <Typography variant="l2dh6">Paramètres du dossier</Typography>
                                }
                            />
                        }
                    >
                        <Box sx={{ gap: '4px', display: 'grid' }}>
                            <Box>
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Référence interne du dossier
                                </Typography>
                                <Typography
                                    sx={{ marginLeft: '5px' }}
                                    variant="l2dbodys"
                                    color="text.secondary"
                                >
                                    (facultatif)
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth
                                defaultValue={refInterne}
                                placeholder="Saisissez une référence pour ce dossier"
                                sx={{ gap: '8px' }}
                                error={!!refInterneError}
                                helperText={refInterneError}
                                onChange={(e) => {
                                    setRefInterneError(
                                        e.target.value.length > 50
                                            ? 'La référence ne peut pas dépasser 50 caractères'
                                            : '',
                                    );
                                }}
                                onBlur={(
                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                                ) => {
                                    if (e.target.value.length <= 50) {
                                        setRefInterne(e.target.value || null);
                                    }
                                }}
                            />
                        </Box>
                        <Box sx={{ gap: '4px', display: 'grid' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    padding: '10px 10px 10px 0px',
                                    alignItems: 'start',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Marquer ce dossier comme urgent ?
                                </Typography>

                                <Switch
                                    size="small"
                                    aria-label="switch"
                                    value={dossierUrgent}
                                    onChange={(e, checked: boolean) => {
                                        setDossierUrgent(checked);
                                    }}
                                />
                            </Box>
                            <Box>
                                <NoteInformation
                                    title="Cette option entraînera des frais de traitement supplémentaires."
                                    color={{
                                        bg: alpha(theme.palette.info.main, 0.1),
                                        border: alpha(theme.palette.info.main, 0.3),
                                    }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ gap: '4px', display: 'grid' }}>
                            <Box>
                                <Typography
                                    variant="l2dbodys"
                                    fontWeight={500}
                                >
                                    Message au formaliste
                                </Typography>
                                <Typography
                                    sx={{ marginLeft: '5px' }}
                                    variant="l2dbodys"
                                    color="text.secondary"
                                >
                                    (facultatif)
                                </Typography>
                            </Box>
                            <TextField
                                placeholder="Ajouter des informations complémentaires au dossier"
                                multiline
                                style={{
                                    width: '100%',
                                }}
                                maxRows={Infinity}
                                defaultValue={commentaires}
                                onBlur={(
                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                                ) => {
                                    if (e.target.value && e.target.value !== '') {
                                        setCommentaires(e.target.value);
                                    } else {
                                        setCommentaires(null);
                                    }
                                }}
                            />
                        </Box>
                    </CardSection>

                    <CardSection
                        sx={{ margin: 0 }}
                        sxContent={{ gap: '24px', display: 'grid' }}
                        header={
                            <CardHeader
                                title={<Typography variant="l2dh6">Facturation</Typography>}
                                headerSx={{ paddingTop: '24px' }}
                                sx={{
                                    minHeight: '36px',
                                    borderLeft: `solid 2px ${theme.palette.primary.main}`,
                                }}
                            />
                        }
                    >
                        <RecapBillingBody
                            prescripteur={prescripteur}
                            premierDossier={premierDossier}
                            sousClients={sousClients}
                            sousClient={sousClient}
                            collection={collection}
                            refInterne={refInterne}
                            commentaires={commentaires}
                            dossierUrgent={dossierUrgent}
                            onChange={onChange}
                        />
                    </CardSection>

                    {innerDossier && innerDossier.documents && (
                        <RecapDocuments
                            innerDossier={innerDossier}
                            prescripteur={prescripteur}
                        />
                    )}
                </Grid>
                <Grid
                    item
                    lg={4}
                    display="flex"
                    flexDirection="column"
                    gap="16px"
                >
                    <RecapLignesArticles
                        dossierPrestations={premierDossier.prestations}
                        collection={collection}
                    />
                    <Box>
                        <FlexyButton
                            icon={sendIcon}
                            label="Envoyer le dossier"
                            onClick={onSubmitHandler}
                            disabled={!!refInterneError}
                            sx={{
                                width: '100%',
                                height: '56px',
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default RecapFormalityShow;
